<template>
  <v-dialog v-model="dialog" width="800px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card :tile="$vuetify.breakpoint.xsOnly" class="d-flex flex-column">
      <v-card-title class="indigo white--text px-3">
        <span class="headline">API Tokens</span>
        <v-spacer />
        <v-btn color="success" @click="dialogCreate = true">
          <v-icon class="mr-2">add</v-icon>
          Nova Chave Secreta
        </v-btn>
        <v-btn icon class="hidden-sm-and-up ml-2" color="white" @click="cancel()">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="mt-4" v-if="secret">
        <v-card color="teal" dark>
          <v-card-title>Salve sua chave secreta!</v-card-title>
          <v-card-text>
            Por favor, salve sua chave secreta em um local seguro. <b>Ela não será exibida novamente!</b>
            Zele por sua segurança, pois ela pode ser usada por qualquer pessoa para acessar a API e realizar operações.
            Se você perder a chave, será necessário criar uma nova.
          </v-card-text>
          <v-card-text>
            <v-text-field
              label="API Token"
              :value="secret"
              dark
              readonly
              hide-details
              hide-spin-buttons
              flat
              outlined
              style="font-family: monospace; font-weight: bold;"
              append-icon="content_copy"
              ref="token"
              @click="copy()"
            />
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-data-table
        :headers="columns"
        :items="tokens"
        disable-pagination
        fixed-header
        hide-default-footer
        class="elevation-0"
        light
      >
        <template v-slot:item.masked="props">
          <span style="font-family: 'Courier New', Courier, monospace; font-weight: bold;">{{ props.item.masked }}</span>
        </template>
        <template v-slot:item.validity="props">
          <span>{{ formatDate(props.item.validity) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            @click="remove(item)"
            color="error"
          >
            remove_circle_outline
          </v-icon>
        </template>
      </v-data-table>

      <v-alert type="error" icon="warning" :value="error" transition="scale-transition" class="mx-4">
        {{ message }}
      </v-alert>
    </v-card>

    <v-dialog v-model="dialogCreate" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Criar API Token</span>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-container>
            <v-form v-model="validate">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="token.label"
                    label="Nome"
                    :rules="[ v => !!v ]"
                    required
                    hint="Precisa ser único"
                  />
                </v-col>
                <v-col cols="12">
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="token.validity"
                        label="Validade"
                        prepend-icon="event"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="token.validity"
                      :active-picker.sync="picker"
                      :max="new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString().slice(0, 10)"
                      :min="new Date().toISOString().slice(0, 10)"
                      width="428px"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-radio-group v-model="token.type" mandatory class="my-0 py-0">
                    <v-radio value="MCP" label="Model Context Protocol (MCP)" />
                    <v-radio value="AUTOMATON" label="Autômato da plataforma (Sentinela)" />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            text
            @click="close()"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            large
            @click="save()"
            :disabled="!validate"
          >
            <v-icon class="mr-2">done</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" timeout="2000" color="success" min-width="0px">API Token copiado para a área de transferência!</v-snackbar>

    <confirm-wrapper ref="confirm" />
  </v-dialog>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

import EmbrapaHelper from '@/helpers/embrapa'
import ErrorHelper from '@/helpers/error'

import ConfirmWrapper from '@/components/ConfirmDialog.vue'

export default {
  mixins: [
    EmbrapaHelper,
    ErrorHelper
  ],
  components: {
    ConfirmWrapper
  },
  data: () => ({
    dialog: false,
    user: null,
    tokens: [],
    dialogCreate: false,
    columns: [
      { text: 'Nome', align: 'start', value: 'label' },
      { text: 'Token', value: 'masked' },
      { text: 'Tipo', value: 'type' },
      { text: 'Validade', value: 'validity' },
      { text: 'Autor', value: 'author' },
      { text: '', value: 'actions', sortable: false }
    ],
    saving: false,
    headers: {},
    error: false,
    message: '',
    token: {
      label: '',
      type: 'MCP',
      validity: ''
    },
    validate: false,
    loading: false,
    picker: null,
    menu: false,
    secret: null,
    snackbar: false
  }),
  beforeMount () {
    this.user = this.$localStorage.get('user')

    this.headers = {
      Authorization: 'Bearer ' + this.user.token
    }

    this.reload()
  },
  mounted () {
    if (this.user.authenticated) {
      this.headers = {
        Authorization: 'Bearer ' + this.$localStorage.get('user').token
      }
    }
  },
  methods: {
    open () {
      this.reload()

      if (!navigator.onLine) {
        this.$emit('message', 'É necessário uma conexão com a internet para prosseguir! Por favor, verifique suas configurações de rede ou tente novamente mais tarde.', 'error')

        return
      }

      this.refresh()
    },
    refresh () {
      const err = error => {
        this.$emit('message', this.errorMessage(error), 'error')
      }

      const api = process.env.VUE_APP_API

      axios.get(api + '/status', { timeout: 12000 }).then(response => {
        axios.get(api + '/manager/tokens', { headers: this.headers }).then(response => {
          this.tokens = response.data

          this.dialog = true
        }).catch(err)
      }).catch(err)
    },
    reload () {
      this.saving = false

      this.secret = null

      this.tokens = []

      this.token.label = ''
      this.token.type = 'MCP'
      this.token.validity = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10)

      this.error = false
      this.message = ''
    },
    cancel () {
      this.dialog = false
    },
    close () {
      this.dialogCreate = false

      this.secret = null

      this.token.label = ''
      this.token.type = 'MCP'
      this.token.validity = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10)
    },
    remove (item) {
      this.$refs.confirm.open(
        'Remover API Token?',
        'A chave secreta será removida permanentemente e não poderá ser recuperada. Tem certeza que deseja continuar?',
        { color: 'error', width: 500, persistent: true }).then((confirm) => {
        if (confirm) {
          axios.delete(process.env.VUE_APP_API + '/manager/token/' + item.id, { headers: this.headers }).then(response => {
            this.refresh()

            this.$emit('message', 'API Token removido com sucesso!', 'success')
          }).catch(error => {
            this.$emit('message', error, 'error')
          })
        }
      })
    },
    change (item) {
      this.manager.email = item.email
      this.manager.role = item.role

      this.role()
    },
    formatDate (date) {
      return moment(date).format('DD/MM/YY')
    },
    save () {
      axios.post(process.env.VUE_APP_API + '/manager/token', this.token, { headers: this.headers }).then(response => {
        this.refresh()

        this.close()

        this.secret = response.data.token

        this.$emit('message', 'Novo API Token gerado com sucesso!', 'success')
      }).catch(error => {
        this.$emit('message', error, 'error')
      })
    },
    copy () {
      const e = this.$refs.token.$el.querySelector('input')

      navigator.clipboard.writeText(e.value).then(() => {
        this.snackbar = true
      }).catch((err) => {
        console.error('Falha ao copiar:', err)
      })
    }
  },
  watch: {
    menu (val) {
      val && setTimeout(() => (this.picker = 'YEAR'))
    }
  }
}
</script>
